<template>
  <div v-if="show" class="app-modal" role="presentation">
    <div class="app-modal__backdrop" @click="emitClose"></div>

    <section
      class="app-modal__dialog"
      role="dialog"
      aria-modal="true"
      aria-labelledby="expense-modal-title"
      @click.stop
    >
      <header class="app-modal__header">
        <div>
          <h3 id="expense-modal-title">{{ modalTitle }}</h3>
          <p>{{ modalDescription }}</p>
        </div>
        <button
          class="app-modal__close"
          type="button"
          :aria-label="text.modal.closeAriaLabel"
          @click="emitClose"
        >
          <i class="fa fa-times" aria-hidden="true"></i>
        </button>
      </header>

      <div class="app-modal__body">
          <aside class="app-modal__menu" :aria-label="text.modal.sectionsAriaLabel">
            <button
            v-for="section in resolvedSections"
              :key="section.key"
              class="app-modal__menu-item"
              :class="{ active: activeSectionKey === section.key }"
              type="button"
              @click="activeSectionKey = section.key"
            >
              <span class="app-modal__menu-icon">
                <i class="fa" :class="section.icon" aria-hidden="true"></i>
              </span>
              <span class="app-modal__menu-copy">
                <strong>{{ section.title }}</strong>
                <small>{{ section.description }}</small>
              </span>
            </button>
          </aside>

          <div class="app-modal__content">
            <form class="app-modal__form" @submit.prevent="submitForm">
              <ExpenseModalSectionGeneral
                :active="activeSectionKey === 'general'"
                :form="form"
                :options="options"
                :is-edit-mode="isEditMode"
                :is-expense-paid="isExpensePaid"
                :status-label="expenseStatusLabel"
                :is-hydrating="isHydratingInitialData"
                :tax-preview="taxPreview"
                :tax-preview-loading="taxPreviewLoading"
                :first-installment-options="firstInstallmentOptions"
                :warnings="warnings"
                :v$="v$"
              />

              <ExpenseModalSectionOperational
                :active="activeSectionKey === 'operational'"
                :form="form"
                :options="options"
                :hide-sale-customer-fields="Number(lockedSaleId || 0) > 0"
              />

              <ExpenseModalSectionDetails
                :active="activeSectionKey === 'details'"
                :form="form"
                :options="options"
              />

              <ExpenseModalSectionFiles
                :active="activeSectionKey === 'files'"
                :form="form"
              />

              <footer class="app-modal__footer">
                <div class="app-modal__footer-right">
                  <button type="button" class="btn btn-light" @click="emitClose">{{ text.modal.cancel }}</button>
                  <button type="submit" class="btn btn-primary">
                    {{ submitting ? text.modal.submitting : submitButtonLabel }}
                  </button>
                </div>
              </footer>
            </form>
          </div>
      </div>
    </section>
  </div>
</template>

<script>
import taxesService from '@/services/taxes';
import { useVuelidate } from '@vuelidate/core';
import { helpers } from '@vuelidate/validators';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  EXPENSE_MODAL_SECTIONS,
  buildExpensePayload,
  buildExpenseTaxPreviewPayload,
  buildFirstInstallmentOptions,
  createInitialExpenseForm,
  createInitialExpenseModalOptions,
  createInitialExpenseTaxPreview,
} from '@/composables/expenses/useExpenseModalForm';
import { fetchExpenseModalCatalogs } from '@/composables/expenses/useExpenseModalCatalogs';
import ExpenseModalSectionGeneral from '@/components/expenses/modal/ExpenseModalSections/ExpenseModalSectionGeneral.vue';
import ExpenseModalSectionOperational from '@/components/expenses/modal/ExpenseModalSections/ExpenseModalSectionOperational.vue';
import ExpenseModalSectionFiles from '@/components/expenses/modal/ExpenseModalSections/ExpenseModalSectionFiles.vue';
import ExpenseModalSectionDetails from '@/components/expenses/modal/ExpenseModalSections/ExpenseModalSectionDetails.vue';
import {
  applyExpenseEditPayloadFallbacks,
  hydrateExpenseFormFromInitialData,
} from '@/composables/expenses/useExpenseModalHydration';
import {
  applyExpenseFeeLockedSaleContext,
  resolveExpenseFeePaidState,
  resolveExpenseFeeStatusLabel,
  applyExpenseFeeTaxSelectionFromDetail,
  enforceExpenseFeeAmountConstraints,
  focusExpenseFeeFirstInvalidField,
  resolveExpenseFeeFirstInvalidField,
} from '@/composables/expensesFees/shared/useExpenseFeeModalHydrationShared';
import { extractPayload } from '@/utils/dataNormalization';
import {
  toPositiveOptionId,
} from '@/utils/optionValues';
import '@vuepic/vue-datepicker/dist/main.css';

const { catalog: text } = useDomainText('expenses');

export default {
  name: 'ExpenseModal',
  components: {
    ExpenseModalSectionGeneral,
    ExpenseModalSectionOperational,
    ExpenseModalSectionFiles,
    ExpenseModalSectionDetails,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    mode: {
      type: String,
      default: 'create',
    },
    initialData: {
      type: Object,
      default: null,
    },
    submitting: {
      type: Boolean,
      default: false,
    },
    lockedSaleId: {
      type: Number,
      default: 0,
    },
  },
  emits: ['close', 'submit'],
  data() {
    return {
      text,
      sections: EXPENSE_MODAL_SECTIONS,
      activeSectionKey: 'general',
      optionsLoading: false,
      taxPreviewLoading: false,
      taxPreviewRequestToken: 0,
      taxPreviewDebounceTimer: null,
      taxPreviewDebounceMs: 350,
      isHydratingInitialData: false,
      warnings: {
        discount: '',
        deductions: '',
      },
      isExpensePaid: false,
      expenseStatusLabel: '',
      warningLocks: {
        discount: false,
        deductions: false,
      },
      form: createInitialExpenseForm(),
      options: createInitialExpenseModalOptions(),
      firstInstallmentOptions: buildFirstInstallmentOptions(),
      taxPreview: createInitialExpenseTaxPreview(),
    };
  },
  computed: {
    isEditMode() {
      return String(this.mode || '').trim().toLowerCase() === 'edit';
    },
    modalTitle() {
      return this.isEditMode ? this.text.modal.titleEdit : this.text.modal.titleCreate;
    },
    modalDescription() {
      return this.isEditMode
        ? this.text.modal.subtitleEdit
        : this.text.modal.subtitleCreate;
    },
    submitButtonLabel() {
      return this.isEditMode ? this.text.modal.submitEdit : this.text.modal.submitCreate;
    },
    resolvedSections() {
      const sectionTextByKey = {
        general: this.text.modal.sections.general,
        operational: this.text.modal.sections.operational,
        details: this.text.modal.sections.details,
        files: this.text.modal.sections.files,
      };

      return this.sections.map((section) => {
        const copy = sectionTextByKey[section.key] || {};

        return {
          ...section,
          title: copy.title || section.key,
          description: copy.description || '',
        };
      });
    },
    hasSupplier() {
      return toPositiveOptionId(this.form.supplier) > 0;
    },
    hasLockedSaleContext() {
      return !this.isEditMode && Number(this.lockedSaleId || 0) > 0;
    },
    hasConcept() {
      return String(this.form.concept || '').trim() !== '';
    },
    hasValidAmount() {
      return Number(this.form.amount || 0) > 0;
    },
    requiresRecurringConfig() {
      return String(this.form.frequency || '').trim().toLowerCase() !== 'unique';
    },
    hasRecurringConfig() {
      if (!this.requiresRecurringConfig) {
        return true;
      }

      const frequency = String(this.form?.frequency || '').trim().toLowerCase();
      const hasCount = Number(this.form.expensesCount || 0) > 0;
      const hasFirstInstallment = String(this.form?.firstInstallmentOption?.value || '').trim() !== '';
      const hasPeriodicity = String(this.form?.periodicityOption?.value || '').trim() !== '';

      if (frequency === 'monthly') {
        return hasCount && hasFirstInstallment;
      }

      if (frequency === 'multiple') {
        return hasCount && hasPeriodicity;
      }

      return hasCount && hasFirstInstallment && hasPeriodicity;
    },
  },
  validations() {
    return {
      form: {
        supplier: {
          required: helpers.withMessage(
            this.text.modal.validation.supplierRequired,
            (value) => toPositiveOptionId(value) > 0
          ),
        },
        concept: {
          required: helpers.withMessage(
            this.text.modal.validation.conceptRequired,
            (value) => String(value || '').trim() !== ''
          ),
        },
        amount: {
          validAmount: helpers.withMessage(
            this.text.modal.validation.amountInvalid,
            (value) => Number(value || 0) > 0
          ),
        },
        expensesCount: {
          validRecurringCount: helpers.withMessage(
            this.text.modal.validation.recurringCountInvalid,
            () => Number(this.form?.expensesCount || 0) > 0
          ),
        },
        firstInstallmentOption: {
          requiredForMonthly: helpers.withMessage(
            this.text.modal.validation.firstInstallmentRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'monthly') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
        periodicityOption: {
          requiredForMultiple: helpers.withMessage(
            this.text.modal.validation.periodicityRequired,
            (value) => {
              const frequency = String(this.form?.frequency || '').trim().toLowerCase();
              if (frequency !== 'multiple') {
                return true;
              }

              return String(value?.value || '').trim() !== '';
            }
          ),
        },
      },
    };
  },
  setup() {
    const v$ = useVuelidate();

    return { v$ };
  },
  watch: {
    show(visible) {
      if (visible) {
        this.handleOpen();
        return;
      }

      this.resetState();
      this.v$.$reset();
    },
    'form.amount'() {
      this.enforceAmountConstraints('amount');
      this.scheduleTaxPreview();
    },
    'form.discount'() {
      this.enforceAmountConstraints('discount');
      this.scheduleTaxPreview();
    },
    'form.deductions'() {
      this.enforceAmountConstraints('deductions');
      this.scheduleTaxPreview();
    },
    'form.expenseDate'(nextValue) {
      this.syncPaymentDateWithExpenseDate(nextValue);
    },
    'form.taxType'() {
      this.scheduleTaxPreview();
    },
    'form.taxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
    'form.taxGroup'() {
      this.scheduleTaxPreview();
    },
    'form.withoutTaxes'() {
      this.scheduleTaxPreview();
    },
    'form.localTaxes': {
      deep: true,
      handler() {
        this.scheduleTaxPreview();
      },
    },
  },
  beforeUnmount() {
    this.clearTaxPreviewDebounce();
  },
  methods: {
    cloneDateValue(value) {
      if (value instanceof Date) {
        return new Date(value.getTime());
      }

      if (typeof value === 'string') {
        return String(value);
      }

      return value;
    },
    syncPaymentDateWithExpenseDate(nextExpenseDate) {
      if (!this.show || this.isEditMode) {
        return;
      }

      this.form.paymentDate = this.cloneDateValue(nextExpenseDate);
    },
    resetState() {
      this.activeSectionKey = this.sections[0].key;
      this.form = createInitialExpenseForm();
      this.options = createInitialExpenseModalOptions();
      this.firstInstallmentOptions = buildFirstInstallmentOptions();
      this.taxPreview = createInitialExpenseTaxPreview();
      this.taxPreviewRequestToken = 0;
      this.taxPreviewLoading = false;
      this.optionsLoading = false;
      this.warnings = {
        discount: '',
        deductions: '',
      };
      this.isExpensePaid = false;
      this.expenseStatusLabel = '';
      this.warningLocks = {
        discount: false,
        deductions: false,
      };
      this.isHydratingInitialData = false;
      this.clearTaxPreviewDebounce();
    },
    async handleOpen() {
      this.resetState();
      await this.loadOptions();

      if (this.initialData && typeof this.initialData === 'object') {
        this.isHydratingInitialData = true;
        const hydratedData = hydrateExpenseFormFromInitialData({
          initialData: this.initialData,
          form: this.form,
          options: this.options,
          firstInstallmentOptions: this.firstInstallmentOptions,
          applyTaxSelectionFromDetail: this.applyTaxSelectionFromDetail,
        });
        await this.$nextTick();
        this.applyTaxSelectionFromDetail(hydratedData);
        this.applyCreateDefaultTaxFromHydratedData(hydratedData);
        this.expenseStatusLabel = resolveExpenseFeeStatusLabel({
          data: hydratedData,
          statusLabels: this.text?.modal?.statusLabels,
        });
        this.isExpensePaid = resolveExpenseFeePaidState({
          data: {
            ...hydratedData,
            status: this.expenseStatusLabel,
          },
        });
        this.isHydratingInitialData = false;
      }

      this.v$.$reset();

      this.applyLockedSaleContext();

      this.scheduleTaxPreview();
    },
    hasExplicitTaxSelectionInData(data = {}) {
      return (
        Object.prototype.hasOwnProperty.call(data, 'without_taxes')
        || Object.prototype.hasOwnProperty.call(data, 'withoutTaxes')
        || Object.prototype.hasOwnProperty.call(data, 'no_taxes')
        || Object.prototype.hasOwnProperty.call(data, 'tax_type')
        || Object.prototype.hasOwnProperty.call(data, 'taxType')
        || Object.prototype.hasOwnProperty.call(data, 'tax_id')
        || Object.prototype.hasOwnProperty.call(data, 'tax_group_id')
        || Object.prototype.hasOwnProperty.call(data, 'tax_ids')
        || Object.prototype.hasOwnProperty.call(data, 'tax')
        || Object.prototype.hasOwnProperty.call(data, 'taxes')
        || Object.prototype.hasOwnProperty.call(data, 'tax_group')
      );
    },
    resolveDefaultTaxOption() {
      return (Array.isArray(this.options.taxes) ? this.options.taxes : [])
        .find((option) => Number(option?.meta?.default || 0) === 1) || null;
    },
    applyCreateDefaultTaxFromHydratedData(data = {}) {
      if (this.isEditMode || this.hasExplicitTaxSelectionInData(data)) {
        return;
      }

      const hasIndividualTax = Array.isArray(this.form.taxes)
        && this.form.taxes.some((option) => toPositiveOptionId(option) > 0);
      const hasTaxGroup = toPositiveOptionId(this.form.taxGroup) > 0;
      if (hasIndividualTax || hasTaxGroup) {
        return;
      }

      const defaultTax = this.resolveDefaultTaxOption();
      if (!defaultTax) {
        return;
      }

      this.form.withoutTaxes = false;
      this.form.taxType = 'individual';
      this.form.taxGroup = null;
      this.form.taxes = [defaultTax];
    },
    emitClose() {
      this.$emit('close');
    },
    clearTaxPreviewDebounce() {
      if (!this.taxPreviewDebounceTimer) {
        return;
      }

      clearTimeout(this.taxPreviewDebounceTimer);
      this.taxPreviewDebounceTimer = null;
    },
    scheduleTaxPreview() {
      if (!this.show) {
        return;
      }

      this.clearTaxPreviewDebounce();
      this.taxPreviewDebounceTimer = setTimeout(() => {
        this.taxPreviewDebounceTimer = null;
        this.fetchTaxPreview();
      }, this.taxPreviewDebounceMs);
    },
    async loadOptions() {
      this.optionsLoading = true;

      try {
        const catalogs = await fetchExpenseModalCatalogs();
        this.options = {
          ...this.options,
          ...catalogs,
        };

        const firstCurrency = this.options.currencies[0]?.value;
        if (firstCurrency && String(this.form.currencyCode || '').trim() === '') {
          this.form.currencyCode = String(firstCurrency).trim().toUpperCase();
        }

        const defaultTax = (Array.isArray(this.options.taxes) ? this.options.taxes : [])
          .find((option) => Number(option?.meta?.default || 0) === 1);
        if (defaultTax && String(this.form?.taxType || '').trim().toLowerCase() === 'individual') {
          this.form.taxes = [defaultTax];
        }
      } finally {
        this.optionsLoading = false;
      }
    },
    applyTaxSelectionFromDetail(data = {}) {
      this.options = applyExpenseFeeTaxSelectionFromDetail({
        data,
        form: this.form,
        options: this.options,
        taxFallbackLabel: this.text?.sections?.taxes?.fallbackTaxLabel,
        taxGroupFallbackLabel: this.text?.sections?.taxes?.fallbackGroupWithIdLabel,
      });
    },
    applyLockedSaleContext() {
      applyExpenseFeeLockedSaleContext({
        form: this.form,
        options: this.options,
        lockedSaleId: this.lockedSaleId,
        fallbackSaleLabel: this.text?.modal?.fallbackSaleLabel,
      });
    },
    enforceAmountConstraints(sourceField = '') {
      enforceExpenseFeeAmountConstraints({
        form: this.form,
        warnings: this.warnings,
        warningLocks: this.warningLocks,
        sourceField,
        warningMessages: this.text?.modal?.warnings,
      });
    },
    async fetchTaxPreview() {
      const requestToken = this.taxPreviewRequestToken + 1;
      this.taxPreviewRequestToken = requestToken;
      this.taxPreviewLoading = true;

      try {
        const response = await taxesService.preview(
          buildExpenseTaxPreviewPayload(this.form),
          { skipLoading: true }
        );
        if (requestToken !== this.taxPreviewRequestToken) {
          return;
        }

        const payload = extractPayload(response);
        this.taxPreview = {
          ...createInitialExpenseTaxPreview(),
          ...(payload && typeof payload === 'object' ? payload : {}),
        };
      } catch (error) {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreview = createInitialExpenseTaxPreview();
        }
      } finally {
        if (requestToken === this.taxPreviewRequestToken) {
          this.taxPreviewLoading = false;
        }
      }
    },
    submitForm() {
      if (this.submitting) {
        return;
      }

      this.v$.$touch();

      if (this.v$.$invalid) {
        this.activeSectionKey = 'general';
        this.focusFirstInvalidField();

        return;
      }

      let payload = buildExpensePayload(this.form, this.taxPreview);
      payload = applyExpenseEditPayloadFallbacks({
        payload,
        isEditMode: this.isEditMode,
        initialData: this.initialData,
      });

      if (this.hasLockedSaleContext) {
        payload.sale_id = Number(this.lockedSaleId || 0);
      }

      this.$emit('submit', payload);
    },
    resolveFirstInvalidGeneralField() {
      return resolveExpenseFeeFirstInvalidField({
        validation: this.v$?.form,
        orderedFields: [
          'supplier',
          'concept',
          'amount',
          'expensesCount',
          'firstInstallmentOption',
          'periodicityOption',
        ],
      });
    },
    focusFirstInvalidField() {
      const invalidField = this.resolveFirstInvalidGeneralField();
      if (invalidField === '') {
        return;
      }
      focusExpenseFeeFirstInvalidField({
        vm: this,
        invalidField,
        selectorMap: {
          supplier: [
            '[data-focus-field="supplier"] .multiselect__input',
            '[data-focus-field="supplier"] .multiselect__tags',
          ],
          concept: ['textarea[data-focus-field="concept"]'],
          amount: ['#expense-modal-amount'],
          expensesCount: ['input[data-focus-field="expensesCount"]'],
          firstInstallmentOption: [
            '[data-focus-field="firstInstallmentOption"] .multiselect__input',
            '[data-focus-field="firstInstallmentOption"] .multiselect__tags',
          ],
          periodicityOption: [
            '[data-focus-field="periodicityOption"] .multiselect__input',
            '[data-focus-field="periodicityOption"] .multiselect__tags',
          ],
        },
      });
    },
  },
};
</script>


